import { useCallback, useEffect, useRef } from "react";

import { useAuth } from "../../context/AuthContext";
import { useThemeToggle } from "../../context/ThemeContext";

/**
 * Liga a aparencia escolhida pelo usuario ao perfil dele no servidor.
 *
 * Por que existe como componente e nao dentro do ThemeContext: o
 * `ThemeContextProvider` ENVOLVE o `AuthProvider` (App.jsx), entao la em cima
 * o usuario ainda nao existe. Este componente vive dentro dos dois e faz a
 * ponte, sem exigir reordenar os providers. Nao renderiza nada.
 *
 * A REGRA QUE NAO PODE SER QUEBRADA (bug real de 2026-08-04): o campo
 * `preferencias` so vem no /api/me — a resposta do LOGIN nao o traz. Um
 * usuario sem a CHAVE `preferencias` significa "ainda nao sei o que o servidor
 * tem", e nesse estado NADA pode ser gravado. A primeira versao tratava
 * chave-ausente como servidor-vazio e subia os padroes do dispositivo novo
 * (claro/violeta) por cima da escolha que o usuario salvou no outro — o
 * relato exato do dono: "volta pro tema branco e cor roxa e fica resetando a
 * cada login". So `preferencias` PRESENTE e vazia autoriza subir o local.
 */

const ESPERA_ANTES_DE_GRAVAR_MS = 800;

function aparenciasIguais(a, b) {
  if (!a || !b) return false;
  return (
    a.tema === b.tema &&
    a.accent === b.accent &&
    a.densidade === b.densidade &&
    a.animacoes === b.animacoes
  );
}

export default function SincronizadorPreferencias() {
  const { usuario, authenticated, request } = useAuth();
  const {
    aparenciaAtual,
    aplicarAparenciaDoServidor,
    resetarHidratacao,
    hidratadoDoServidor,
  } = useThemeToggle();

  // Ultima aparencia confirmada no servidor (aplicada ou gravada por nos).
  const ultimaSalvaRef = useRef(null);
  // Snapshot do objeto `preferencias` inteiro que veio do servidor. Usado ao
  // gravar, para nao apagar namespaces que este componente nao gerencia
  // (ex.: `navegacao` no futuro) — o PUT substitui o campo todo.
  const preferenciasServidorRef = useRef(null);
  // Evita subir o local mais de uma vez por usuario quando o servidor esta
  // genuinamente vazio.
  const uploadInicialFeitoRef = useRef(null);

  const gravar = useCallback(
    async (aparencia) => {
      try {
        const base =
          preferenciasServidorRef.current &&
          typeof preferenciasServidorRef.current === "object"
            ? preferenciasServidorRef.current
            : {};
        const corpo = { ...base, versao: 1, aparencia };
        await request("/api/me/dashboard-preferencia", {
          method: "PUT",
          body: JSON.stringify({ preferencias_ui: corpo }),
          // Preferencia visual falhando nao merece toast vermelho: o usuario
          // nao pediu "salvar", so trocou uma cor. O espelho local ja garante
          // a escolha neste dispositivo; tentamos de novo na proxima mudanca.
          silentError: true,
        });
        ultimaSalvaRef.current = aparencia;
        preferenciasServidorRef.current = corpo;
      } catch {
        // Sem servidor, a aparencia continua valendo localmente.
      }
    },
    [request],
  );

  // --- 1. hidratacao / reconciliacao ---------------------------------------
  // Roda a cada mudanca do objeto usuario (login, loadMe, visibilitychange).
  useEffect(() => {
    if (!authenticated || !usuario) {
      // Logout: o proximo usuario nao pode herdar nada deste.
      ultimaSalvaRef.current = null;
      preferenciasServidorRef.current = null;
      uploadInicialFeitoRef.current = null;
      return;
    }

    // A resposta do login nao traz `preferencias`; so o /api/me traz. Sem a
    // chave, NAO sabemos o estado do servidor — nao aplicar e, sobretudo,
    // NAO gravar. O loadMe chega logo em seguida e ai sim decidimos.
    if (usuario.preferencias === undefined) return;

    preferenciasServidorRef.current =
      usuario.preferencias && typeof usuario.preferencias === "object"
        ? usuario.preferencias
        : {};

    const doServidor = usuario.preferencias?.aparencia;
    const servidorTemAparencia =
      doServidor && typeof doServidor === "object" && Object.keys(doServidor).length > 0;

    if (servidorTemAparencia) {
      // Nao sobrescrever uma edicao local que ainda nao foi gravada: se o
      // usuario acabou de trocar a cor e o debounce nao disparou, o servidor
      // esta atrasado em relacao a tela — quem manda e a tela.
      const edicaoLocalPendente =
        hidratadoDoServidor &&
        ultimaSalvaRef.current &&
        !aparenciasIguais(aparenciaAtual, ultimaSalvaRef.current);

      if (!edicaoLocalPendente && !aparenciasIguais(doServidor, ultimaSalvaRef.current)) {
        aplicarAparenciaDoServidor(doServidor);
        ultimaSalvaRef.current = { ...aparenciaAtual, ...doServidor };
      } else if (!hidratadoDoServidor) {
        aplicarAparenciaDoServidor(null);
      }
      return;
    }

    // Servidor PRESENTE e vazio: primeira vez deste usuario com a
    // sincronizacao. Sobe o que ele ja tinha escolhido neste dispositivo,
    // para ninguem perder configuracao ao ganhar o recurso.
    const id = usuario.id || usuario.email;
    aplicarAparenciaDoServidor(null);
    if (id && uploadInicialFeitoRef.current !== id) {
      uploadInicialFeitoRef.current = id;
      gravar(aparenciaAtual);
    }
    // `aparenciaAtual` fora das dependencias de proposito: este efeito reage
    // ao USUARIO/payload do servidor; mudanca de cor e assunto do efeito 2.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, usuario, aplicarAparenciaDoServidor, gravar, hidratadoDoServidor]);

  // --- 2. persistencia: mudou na tela, grava no servidor -------------------
  useEffect(() => {
    if (!authenticated || !hidratadoDoServidor) return undefined;
    if (aparenciasIguais(aparenciaAtual, ultimaSalvaRef.current)) return undefined;

    // Espera curta porque o seletor de cor dispara varias mudancas seguidas.
    const t = setTimeout(() => gravar(aparenciaAtual), ESPERA_ANTES_DE_GRAVAR_MS);
    return () => clearTimeout(t);
  }, [authenticated, hidratadoDoServidor, aparenciaAtual, gravar]);

  // --- 3. logout: rearma a hidratacao para o proximo login -----------------
  useEffect(() => {
    if (!authenticated && hidratadoDoServidor) resetarHidratacao();
  }, [authenticated, hidratadoDoServidor, resetarHidratacao]);

  return null;
}
